import React from 'react';
import { useTranslation } from 'react-i18next';

import useReportQueryParams from '../../../../hooks/use-report-query-params';
import OverallCurriculumProgressChart from '../../charts/engagement/OverallCurriculumProgressChart';
import TotalActiveStudentsChart from '../../charts/engagement/TotalActiveStudentsChart';
import ActivitiesCompletedTimeSpentChart from '../../charts/engagement/ActivitiesCompletedTimeSpentChart';
import ReportCard from 'components/ReportsModule/components/common/ReportCard';
import { ReportEntityType } from 'components/ReportsModule/types';
import { gql, useFragment, FragmentType } from 'types/__generated__';

export const OrganizationEngagementFragment = gql(/* GraphQL */ `
  fragment OrganizationEngagement on KeyOrganizationEngagementReportResult {
    organization {
      id
      name
    }
    curriculumProgressPercentage
    totalStudentCount
    activeStudentCount
    totalActivitiesCompletedCount
    averageWeeklyMinutesSpent
  }
`);

const EngagementChartView: React.FC<{
  reportData: FragmentType<typeof OrganizationEngagementFragment>[];
  loading: boolean;
}> = ({ loading, ...props }) => {
  const { t } = useTranslation(['Reports', 'ReportsHelpCharts', 'ReportsHelpCommon']);
  const reportData = useFragment(OrganizationEngagementFragment, props.reportData) as any[];
  const [, setQuery] = useReportQueryParams();

  const [
    labels,
    {
      ids,
      curriculumProgressPercentage,
      totalStudentCount,
      activeStudentCount,
      totalActivitiesCompletedCount,
      averageWeeklyMinutesSpent
    }
  ] = reportData.reduce(
    (
      acc,
      {
        organization,
        curriculumProgressPercentage,
        totalStudentCount,
        activeStudentCount,
        totalActivitiesCompletedCount,
        averageWeeklyMinutesSpent
      }
    ) => {
      if (!organization) return acc;
      const [schoolNames, stats] = acc;

      schoolNames.push(organization.name);
      stats.ids.push(organization.id);
      stats.curriculumProgressPercentage.push(curriculumProgressPercentage);
      stats.totalStudentCount.push(totalStudentCount);
      stats.activeStudentCount.push(activeStudentCount);
      stats.totalActivitiesCompletedCount.push(totalActivitiesCompletedCount);
      stats.averageWeeklyMinutesSpent.push(averageWeeklyMinutesSpent);
      return acc;
    },
    [
      [],
      {
        ids: [],
        curriculumProgressPercentage: [],
        activeStudentCount: [],
        totalStudentCount: [],
        totalActivitiesCompletedCount: [],
        averageWeeklyMinutesSpent: []
      }
    ] as [string[], any]
  );

  const handleChartClick = (index: number): void => {
    setQuery({ entityType: ReportEntityType.SCHOOL, entityId: ids[index] }, 'pushIn');
  };

  return (
    <>
      <ReportCard
        title={t('Overall Curriculum Progress')}
        helpText={t('curriculumProgress', { ns: 'ReportsHelpCommon' })}
        loading={loading}
        reportData={reportData}
      >
        <OverallCurriculumProgressChart
          series={curriculumProgressPercentage}
          labels={labels}
          onClick={handleChartClick}
        />
      </ReportCard>
      <ReportCard
        title={t('Total/Active Students')}
        helpText={t('totalAndActiveStudents', { ns: 'ReportsHelpCharts' })}
        loading={loading}
        reportData={reportData}
      >
        <TotalActiveStudentsChart
          activeStudentsSeries={activeStudentCount}
          totalStudentsSeries={totalStudentCount}
          labels={labels}
          onClick={handleChartClick}
        />
      </ReportCard>
      <ReportCard
        title={t('Activities & Average Weekly Time Spent')}
        helpText={t('activitiesCompletedAndTimeSpentBySchoolClass', {
          ns: 'ReportsHelpCharts'
        })}
        loading={loading}
        reportData={reportData}
      >
        <ActivitiesCompletedTimeSpentChart
          activitiesCompletedSeries={totalActivitiesCompletedCount}
          timeSpentSeries={averageWeeklyMinutesSpent}
          labels={labels}
        />
      </ReportCard>
    </>
  );
};

export default EngagementChartView;
